import { useState } from 'react';
import { Share2, Link as LinkIcon, MessageCircle, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

/**
 * ShareButton — usable on products, services, events, shops.
 *
 * Props:
 *   url      — full URL to share (defaults to current page).
 *   title    — short text shown in share sheet / WhatsApp message.
 *   testId   — root data-testid prefix.
 *   variant  — 'icon' (round 36px) | 'inline' (text+icon).
 */
export default function ShareButton({ url, title, testId = 'share', variant = 'icon' }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = title ? `${title} — ${shareUrl}` : shareUrl;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiato!');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Impossibile copiare il link');
    }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || 'Kyloo', url: shareUrl });
      } catch (e) {
        // AbortError when user cancels the OS share sheet — expected, ignore.
        if (e?.name !== 'AbortError') console.debug('Native share failed:', e?.message);
      }
    }
  };

  const trigger =
    variant === 'inline' ? (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-600 transition-colors"
        data-testid={`${testId}-trigger`}
        aria-label="Condividi"
      >
        <Share2 className="w-4 h-4" />
        <span>Condividi</span>
      </button>
    ) : (
      <button
        type="button"
        className="p-2 rounded-full bg-white/95 hover:bg-white text-slate-700 hover:text-blue-600 shadow-md transition-colors"
        data-testid={`${testId}-trigger`}
        aria-label="Condividi"
        onClick={(e) => e.stopPropagation()}
      >
        <Share2 className="w-4 h-4" />
      </button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid={`${testId}-menu`}>
        <DropdownMenuItem onClick={handleCopy} data-testid={`${testId}-copy`}>
          {copied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <LinkIcon className="w-4 h-4 mr-2" />}
          {copied ? 'Copiato!' : 'Copia link'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleWhatsApp} data-testid={`${testId}-whatsapp`}>
          <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" />
          Condividi su WhatsApp
        </DropdownMenuItem>
        {typeof navigator !== 'undefined' && navigator.share && (
          <DropdownMenuItem onClick={handleNativeShare} data-testid={`${testId}-native`}>
            <Share2 className="w-4 h-4 mr-2" />
            Altro…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
